class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.sideCart = document.querySelector('.cart-up-rec');
    this.drawerInner = this.querySelector('.drawer__inner');
    this.observer = null;

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
    this.observeCartChanges();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  setSideCartState(show) {
    this.sideCart = document.querySelector('.cart-up-rec');

    if (!this.sideCart) return;

    if (show) {
      this.sideCart.style.visibility = 'visible';
      this.sideCart.style.opacity = '1';
      this.sideCart.classList.add('active');
    } else {
      this.sideCart.classList.remove('active');
      this.sideCart.style.visibility = 'hidden';
      this.sideCart.style.opacity = '0';
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');

    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }

        setTimeout(() => {
          if (!this.classList.contains('is-empty') && this.classList.contains('active')) {
            this.setSideCartState(true);
          }
        }, 200);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    if (this.observer) this.observer.disconnect();

    this.setSideCartState(false);

    setTimeout(() => {
      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
      this.observeCartChanges();
    }, 300);
  }

  observeCartChanges() {
    if (this.observer) this.observer.disconnect();

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName !== 'class') return;
        if (!this.classList.contains('active')) return;

        const isEmpty = this.classList.contains('is-empty');

        if (isEmpty) {
          this.setSideCartState(false);
        } else {
          setTimeout(() => {
            if (this.classList.contains('active') && !this.classList.contains('is-empty')) {
              this.setSideCartState(true);
            }
          }, 200);
        }
      });
    });

    this.observer.observe(this, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerHtml = parsedState.sections?.['cart-drawer'];
    let isEmpty = false;

    /*
      Read the new server-rendered cart drawer state.
      This removes is-empty as soon as the first item is added.
    */
    if (drawerHtml) {
      const responseDocument = new DOMParser().parseFromString(
        drawerHtml,
        'text/html'
      );

      const responseDrawer = responseDocument.querySelector('cart-drawer');

      if (responseDrawer) {
        isEmpty = responseDrawer.classList.contains('is-empty');
      }
    }

    this.classList.toggle('is-empty', isEmpty);
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHtml = parsedState.sections?.[section.id];

      if (!sectionElement || !sectionHtml) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        sectionHtml,
        section.selector
      );
    });

    this.drawerInner = this.querySelector('.drawer__inner');
    this.sideCart = document.querySelector('.cart-up-rec');

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');

      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }

      this.open();
    }, 200);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const responseDocument = new DOMParser().parseFromString(html, 'text/html');
    const element = responseDocument.querySelector(selector);

    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);